import React, { useState, useEffect } from "react";
import { TextField, Typography, FormControlLabel, RadioGroup, FormControl, Radio, Box, Dialog, Select, MenuItem } from '@mui/material';
import { PersonAvatar } from '../Snippet';
import { Button } from '../Button'
import { Text } from '../Typography';
import { useTranslation } from 'react-i18next';
import { useDataProvider } from "src/mtska-frontend-data-provider";

const BookingFormDialog = ({ open, onClose, onSave, dateRange, warningMessage, possibleDrivers, calendar }) => {
    const { t } = useTranslation();
    const [cta, setCta] = useState(t('Select the driver associated to this booking'));
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [localSaving, setLocalSaving] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);

    // DATA PROVIDER
    const {
        doAction,
        success
    } = useDataProvider('calendars/carcalendarrecord', false);

    const handleSave = async (data) => {
        doAction && doAction({ action: 'create', model: 'calendars/carcalendarrecord', data: data });
    }

    useEffect(() => {
        if (success) {
            onSave()
            setIsCompleted(true);
        }
        setLocalSaving(false);
    }, [success])

    const handleClose = (e) => {
        e.preventDefault();
        if (isCompleted) {
            setIsCompleted(false);
        }
        onClose();
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        const data = {
            bookingStart_at: dateRange[0].format('YYYY-MM-DD'),
            bookingEnd_at: dateRange[1].format('YYYY-MM-DD'),
            isApproved: 'true',
            car_calendar_id: calendar.data._id,
            employment_id: selectedDriverId,
            legal_entity_id: calendar.data.legalEntity._id,
            notes: formData.get('notes')
        }

        handleSave(data);
        setLocalSaving(true);
    };

    useEffect(() => {
        if (possibleDrivers.length === 1) {
            setSelectedDriverId(possibleDrivers[0].employment._id);
            setCta('');
        }
    }, [possibleDrivers]);

    const getCustomMessage = () => {
        const bookableCalendar = calendar?.data;
        if (!bookableCalendar) return;

        return (
            <Box className="bookingInfo">
                {/* <textarea>{JSON.stringify(bookableCalendar)}</textarea> */}
                <p className="customMessage">{bookableCalendar.customMessage}</p>

                <p className="timeframePickup"><span className="label">{t('timeframePickup')}</span><span className="value">{bookableCalendar.timeframePickup}</span></p>
                <p className="timeframeReenter"><span className="label">{t('timeframeReenter')}</span><span className="value">{bookableCalendar.timeframeReenter}</span></p>
            </Box>
        )
    }

    return (
        <Dialog className="dialog dialog-confirm" open={open} onClose={handleClose}>
            <Box className="dialog-backdrop"></Box>
            {isCompleted && (
                <Box className="dialog-content">
                    <Text level="h3" style={{ fontSize: '18px' }}>{t('New booking: {{bookingStart_at}} - {{bookingEnd_at}}', { bookingStart_at: dateRange[0]?.format('DD/MM/YYYY'), bookingEnd_at: dateRange[1]?.format('DD/MM/YYYY') })}</Text>

                    <Text level="body-md" style={{ fontSize: '14px' }}>{t('Booking completed, check your email to find the confirmation message with all needed details.')}</Text>

                    <Box className="dialog-actions" style={{ paddingTop: '30px' }}>
                        <Button variant="solid" className="dialog-ok" onClick={handleClose}>{t('Close')}</Button>
                    </Box>
                </Box>
            )}
            {!isCompleted && (
                <Box className="dialog-content">
                    <Text level="h3" style={{ fontSize: '18px' }}>{t('New booking: {{bookingStart_at}} - {{bookingEnd_at}}', { bookingStart_at: dateRange[0]?.format('DD/MM/YYYY'), bookingEnd_at: dateRange[1]?.format('DD/MM/YYYY') })}</Text>
                    {warningMessage && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            ⚠️ {warningMessage}
                        </Typography>
                    )}

                    <Text level="body-md">{cta}</Text>

                    <form onSubmit={handleSubmit} id="booking-form" style={{ paddingTop: '20px' }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <Select
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxWidth: '80vw', // non superare la larghezza viewport
                                            overflowX: 'auto',  // se il contenuto è troppo largo
                                        },
                                    },
                                }}
                                value={selectedDriverId}
                                onChange={(e) => setSelectedDriverId(e.target.value)}
                                renderValue={(selected) => {
                                    const driver = possibleDrivers.find(d => d.employment._id === selected);
                                    return driver ? (
                                        <PersonAvatar
                                            firstname={driver.employment.person.firstname}
                                            lastname={driver.employment.person.lastname}
                                            withname={true}
                                            detail={false}
                                        />
                                    ) : null;
                                }}
                            >
                                {possibleDrivers.map((driver) => (
                                    <MenuItem key={driver.employment._id} value={driver.employment._id}>
                                        <PersonAvatar
                                            firstname={driver.employment.person.firstname}
                                            lastname={driver.employment.person.lastname}
                                            withname={true}
                                            detail={true}
                                            item={driver}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {
                            getCustomMessage()
                        }

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
                            <TextField label="Note" name="notes" multiline rows={4} fullWidth />
                        </div>
                    </form>

                    <Box className="dialog-actions" style={{ paddingTop: '10px' }}>
                        <Button variant="soft" className="dialog-ko" onClick={onClose}>{t('Cancel')}</Button>
                        <Button variant="solid" className="dialog-ok" type="submit" form="booking-form" disabled={!selectedDriverId || localSaving}>{t('Save')}</Button>
                    </Box>
                </Box>
            )}
        </Dialog>
    )

};

export default BookingFormDialog;